// Host-facing HTTP implementation of ChoicesApi against /v1/choices/*
// (GET /diamonds/balance, POST /diamonds/spend). Firebase-free at the SDK
// layer: auth + base URL come from the injected BackendHttpDeps, so any
// host (PlaygroundHost, Telegram/Steam shells, future partners) can
// construct it.
// Do NOT import from barrel exports (choices/index.ts).

import {
  ChoicesApi,
  ChoicesBalance,
  ChoicesSpendRequest,
  ChoicesSpendResult,
} from './ChoicesApi'
import { RundotApiError, RateLimitedError } from '../errors'
import { authedBackendFetch } from '../http/backendHttpDeps'
import type { BackendHttpDeps } from '../http/backendHttpDeps'

/**
 * Default backoff (seconds) when a 429 response arrives with no
 * `Retry-After` header. Choices sometimes omits the header; without a
 * default, the two client paths would diverge:
 *   - HTTP (this file) would fall back to `NaN * 1000` if we didn't
 *     have a default, then throw an unusable RateLimitedError.
 *   - RPC (H5 bridge → RpcClient) would fall back to RpcClient's
 *     generic 1-second default.
 * Mirrored by `RATE_LIMIT_DEFAULT_BACKOFF_MS = 60_000` in the RN
 * client's `H5ChoicesHandler.ts` so both paths back off identically on
 * the same server response.
 */
const RATE_LIMIT_DEFAULT_RETRY_AFTER_SECONDS = 60

export class HttpChoicesApi implements ChoicesApi {
  private readonly deps: BackendHttpDeps

  constructor(deps: BackendHttpDeps) {
    this.deps = deps
  }

  async getBalance(): Promise<ChoicesBalance> {
    const response = await authedBackendFetch(
      this.deps,
      'GET',
      '/v1/choices/diamonds/balance',
    )
    return this.handleResponse<ChoicesBalance>(response)
  }

  async spend(request: ChoicesSpendRequest): Promise<ChoicesSpendResult> {
    const response = await authedBackendFetch(
      this.deps,
      'POST',
      '/v1/choices/diamonds/spend',
      { ...request },
    )
    return this.handleResponse<ChoicesSpendResult>(response)
  }

  /**
   * Parses the venus response envelope and maps error statuses to
   * `RundotApiError` with the server-side `status` code as the client
   * code. Preserves the HTTP status for callers that need to switch on
   * transport-level information (e.g. 429 vs. 502).
   *
   * 429 gets a dedicated branch: throw `RateLimitedError` with the
   * `Retry-After` header (seconds) converted to ms, so callers can
   * `catch (err) { if (err instanceof RateLimitedError) sleep(err.retryAfterMs) }`.
   * Mirrors the `HttpFilesApi` / `HttpAudioGenApi` pattern.
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (response.ok) {
      return response.json() as Promise<T>
    }

    if (response.status === 429) {
      const retryAfterSec = parseInt(
        response.headers.get('Retry-After')
          ?? String(RATE_LIMIT_DEFAULT_RETRY_AFTER_SECONDS),
        10,
      )
      throw new RateLimitedError(retryAfterSec * 1000)
    }

    const text = await response.text()
    let code = 'UPSTREAM_ERROR'
    let message = text || `Request failed (${response.status})`
    try {
      const parsed: unknown = JSON.parse(text)
      if (parsed !== null && typeof parsed === 'object') {
        const errorBody = parsed as Record<string, unknown>
        if (typeof errorBody.status === 'string') code = errorBody.status
        if (typeof errorBody.message === 'string') message = errorBody.message
      }
    } catch {
      // non-JSON body — keep raw text as the message
    }
    throw new RundotApiError(code, message, response.status)
  }
}
